import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Save, Cloud, TestTube } from "lucide-react";
import { settingsAPI } from "@/services/api";

const Settings = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingCloudStorage, setIsLoadingCloudStorage] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isTestingOpenAI, setIsTestingOpenAI] = useState(false);

  // Account Settings Form
  const accountForm = useForm({
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
      mobileNumber: user?.mobileNumber || "",
    },
  });

  // Email Settings Form
  const emailForm = useForm({
    defaultValues: {
      welcomeEmailTemplate:
        "Welcome to AssessPro! We're glad to have you here.\n\nYour exam has been scheduled for {{examDate}}. Please make sure to log in using the credentials provided to you.",
      reminderEmailTemplate:
        "This is a reminder that your exam is scheduled for {{examDate}}. Make sure you're prepared and ready to go!",
      completionEmailTemplate:
        "Congratulations on completing your exam! Your results will be available shortly.",
    },
  });

  // Security Settings Form
  const securityForm = useForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Cloud Storage Settings Form
  const cloudStorageForm = useForm({
    defaultValues: {
      bucket: "",
      access_key: "",
      secret_key: "",
      region: "us-east-1",
      url: "",
      openai_api_key: "",
    },
  });

  // Load cloud storage settings on component mount
  useEffect(() => {
    if (user?.role === 'company_super_admin') {
      loadCloudStorageSettings();
    }
  }, [user]);

  const loadCloudStorageSettings = async () => {
    setIsLoadingCloudStorage(true);
    try {
      const response = await settingsAPI.getCloudStorageSettings();
      if (response.data.success) {
        const settings = response.data.data;
        cloudStorageForm.reset({
          bucket: settings.bucket || "",
          access_key: settings.access_key || "",
          secret_key: settings.secret_key || "",
          region: settings.region || "us-east-1",
          url: settings.url || "",
          openai_api_key: settings.openai_api_key || "",
        });
      }
    } catch (error) {
      console.error("Failed to load cloud storage settings:", error);
      toast({
        title: "Error",
        description: "Failed to load cloud storage settings",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCloudStorage(false);
    }
  };

  const handleAccountSubmit = (data) => {
    setIsUpdating(true);
    // Simulate API call
    setTimeout(() => {
      toast({
        title: "Settings updated",
        description: "Your account settings have been updated successfully.",
      });
      setIsUpdating(false);
    }, 1000);
  };

  const handleEmailTemplatesSubmit = (data) => {
    setIsUpdating(true);
    // Simulate API call
    setTimeout(() => {
      toast({
        title: "Email templates updated",
        description: "Your email templates have been saved successfully.",
      });
      setIsUpdating(false);
    }, 1000);
  };

  const handleSecuritySubmit = (data) => {
    setIsUpdating(true);

    if (data.newPassword !== data.confirmPassword) {
      toast({
        title: "Password error",
        description: "New password and confirm password must match.",
        variant: "destructive",
      });
      setIsUpdating(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      toast({
        title: "Password updated",
        description: "Your password has been updated successfully.",
      });
      securityForm.reset({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setIsUpdating(false);
    }, 1000);
  };

  const handleCloudStorageSubmit = async (data) => {
    setIsUpdating(true);
    try {
      const response = await settingsAPI.updateCloudStorageSettings(data);
      if (response.data.success) {
        toast({
          title: "Cloud Storage Settings Updated",
          description: "Your cloud storage settings have been saved successfully.",
        });
      }
    } catch (error) {
      console.error("Failed to update cloud storage settings:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update cloud storage settings",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTestCloudStorage = async () => {
    const formData = cloudStorageForm.getValues();
    setIsTesting(true);
    try {
      const response = await settingsAPI.testCloudStorageConnection(formData);
      if (response.data.success) {
        toast({
          title: "Connection Test Successful",
          description: "Cloud storage connection is working properly.",
        });
      }
    } catch (error) {
      console.error("Cloud storage test failed:", error);
      toast({
        title: "Connection Test Failed",
        description: error.response?.data?.message || "Failed to connect to cloud storage",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleTestOpenAI = async () => {
    const formData = cloudStorageForm.getValues();
    setIsTestingOpenAI(true);
    try {
      const response = await settingsAPI.testOpenAIConnection({ 
        openai_api_key: formData.openai_api_key 
      });
      if (response.data.success) {
        toast({
          title: "OpenAI Connection Test Successful",
          description: "OpenAI API key is working properly.",
        });
      }
    } catch (error) {
      console.error("OpenAI test failed:", error);
      toast({
        title: "OpenAI Connection Test Failed",
        description: error.response?.data?.message || "Failed to connect to OpenAI API",
        variant: "destructive",
      });
    } finally {
      setIsTestingOpenAI(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-gray-500">
          Manage your account settings and preferences
        </p>
      </div>

      <Tabs defaultValue="account" className="w-full">
        <TabsList className={`grid w-full ${user?.role === 'company_super_admin' ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="emails">Email Templates</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          {user?.role === 'company_super_admin' && (
            <TabsTrigger value="cloud-storage" className="flex items-center gap-2">
              <Cloud className="h-4 w-4" />
              Cloud Storage
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Account Settings</CardTitle>
              <CardDescription>Update your account information</CardDescription>
            </CardHeader>
            <form onSubmit={accountForm.handleSubmit(handleAccountSubmit)}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="Your name"
                    {...accountForm.register("name")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Your email address"
                    disabled
                    {...accountForm.register("email")}
                  />
                  <p className="text-xs text-gray-500">
                    Email address cannot be changed
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobileNumber">Mobile Number</Label>
                  <Input
                    id="mobileNumber"
                    placeholder="Your mobile number"
                    {...accountForm.register("mobileNumber")}
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>

        <TabsContent value="emails">
          <Card>
            <CardHeader>
              <CardTitle>Email Templates</CardTitle>
              <CardDescription>
                Customize the email templates sent to candidates
              </CardDescription>
            </CardHeader>
            <form onSubmit={emailForm.handleSubmit(handleEmailTemplatesSubmit)}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="welcomeEmailTemplate">Welcome Email</Label>
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="welcomeEmailEnabled" className="text-sm">
                        Enable
                      </Label>
                      <Switch id="welcomeEmailEnabled" defaultChecked />
                    </div>
                  </div>
                  <Textarea
                    id="welcomeEmailTemplate"
                    rows={4}
                    placeholder="Template for welcome emails"
                    {...emailForm.register("welcomeEmailTemplate")}
                  />
                  <p className="text-xs text-gray-500">
                    Variables: {"{{examDate}}"}, {"{{candidateName}}"},{" "}
                    {"{{examName}}"}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="reminderEmailTemplate">
                      Reminder Email
                    </Label>
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="reminderEmailEnabled" className="text-sm">
                        Enable
                      </Label>
                      <Switch id="reminderEmailEnabled" defaultChecked />
                    </div>
                  </div>
                  <Textarea
                    id="reminderEmailTemplate"
                    rows={4}
                    placeholder="Template for reminder emails"
                    {...emailForm.register("reminderEmailTemplate")}
                  />
                  <p className="text-xs text-gray-500">
                    Variables: {"{{examDate}}"}, {"{{candidateName}}"},{" "}
                    {"{{examName}}"}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="completionEmailTemplate">
                      Completion Email
                    </Label>
                    <div className="flex items-center space-x-2">
                      <Label
                        htmlFor="completionEmailEnabled"
                        className="text-sm"
                      >
                        Enable
                      </Label>
                      <Switch id="completionEmailEnabled" defaultChecked />
                    </div>
                  </div>
                  <Textarea
                    id="completionEmailTemplate"
                    rows={4}
                    placeholder="Template for completion emails"
                    {...emailForm.register("completionEmailTemplate")}
                  />
                  <p className="text-xs text-gray-500">
                    Variables: {"{{candidateName}}"}, {"{{examName}}"},{" "}
                    {"{{score}}"}
                  </p>
                </div>
              </CardContent>

              <CardFooter>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Templates
                    </>
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Update your password and security preferences
              </CardDescription>
            </CardHeader>
            <form onSubmit={securityForm.handleSubmit(handleSecuritySubmit)}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    placeholder="Enter current password"
                    {...securityForm.register("currentPassword", {
                      required: true,
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Enter new password"
                    {...securityForm.register("newPassword", {
                      required: true,
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    {...securityForm.register("confirmPassword", {
                      required: true,
                    })}
                  />
                </div>

                <div className="pt-4">
                  <h3 className="text-lg font-medium">
                    Two-Factor Authentication
                  </h3>
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch id="2fa" />
                    <Label htmlFor="2fa">
                      Enable two-factor authentication
                    </Label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Add an extra layer of security to your account by enabling
                    two-factor authentication
                  </p>
                </div>

                <div className="pt-4">
                  <h3 className="text-lg font-medium">Session Settings</h3>
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch id="activeSession" defaultChecked />
                    <Label htmlFor="activeSession">Keep session active</Label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Stay logged in even when the browser is closed
                  </p>
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Update Security Settings
                    </>
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>

        {user?.role === 'company_super_admin' && (
          <TabsContent value="cloud-storage">
            <Card>
              <CardHeader>
                <CardTitle>Cloud Storage Configuration</CardTitle>
                <CardDescription>
                  Configure your AWS S3 cloud storage for file uploads and document storage
                </CardDescription>
              </CardHeader>
              {isLoadingCloudStorage ? (
                <CardContent className="flex justify-center items-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </CardContent>
              ) : (
                <form onSubmit={cloudStorageForm.handleSubmit(handleCloudStorageSubmit)}>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="bucket">S3 Bucket Name</Label>
                        <Input
                          id="bucket"
                          placeholder="your-bucket-name"
                          {...cloudStorageForm.register("bucket", { required: true })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="region">Region</Label>
                        <Input
                          id="region"
                          placeholder="us-east-1"
                          {...cloudStorageForm.register("region", { required: true })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="access_key">Access Key ID</Label>
                      <Input
                        id="access_key"
                        placeholder="AKIAIOSFODNN7EXAMPLE"
                        {...cloudStorageForm.register("access_key", { required: true })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="secret_key">Secret Access Key</Label>
                      <Input
                        id="secret_key"
                        type="password"
                        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                        {...cloudStorageForm.register("secret_key", { required: true })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="url">S3 URL (Optional)</Label>
                      <Input
                        id="url"
                        placeholder="https://your-bucket.s3.us-east-1.amazonaws.com"
                        {...cloudStorageForm.register("url")}
                      />
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="text-lg font-medium mb-4">OpenAI Configuration</h3>
                      <div>
                        <Label htmlFor="openai_api_key">OpenAI API Key</Label>
                        <Input
                          id="openai_api_key"
                          type="password"
                          placeholder="sk-..."
                          {...cloudStorageForm.register("openai_api_key")}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Required for AI-powered resume parsing and question generation features
                        </p>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex space-x-4">
                    <Button type="submit" disabled={isUpdating}>
                      {isUpdating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save Configuration
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestCloudStorage}
                      disabled={isTesting}
                    >
                      {isTesting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Testing S3...
                        </>
                      ) : (
                        <>
                          <TestTube className="mr-2 h-4 w-4" />
                          Test S3 Connection
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestOpenAI}
                      disabled={isTestingOpenAI}
                    >
                      {isTestingOpenAI ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Testing OpenAI...
                        </>
                      ) : (
                        <>
                          <TestTube className="mr-2 h-4 w-4" />
                          Test OpenAI API
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </form>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Settings;
